import React, { useState } from 'react';
import { Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { usePhotos } from '@/hooks/usePhotos';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import PhotoViewer from './PhotoViewer';
import type { ProgressPhoto } from '@/types';

interface PhotoTimelineProps {
  onPhotoClick?: (photo: ProgressPhoto) => void;
}

export default function PhotoTimeline({ onPhotoClick }: PhotoTimelineProps) {
  const { user, session } = useAuth();
  const { photos, loading, error, refresh, removePhoto } = usePhotos();
  const [selectedPhoto, setSelectedPhoto] = useState<ProgressPhoto | null>(null);

  // Check if user is authenticated
  const isAuthenticated = user && session;

  // Listen for photo upload events to refresh the timeline
  React.useEffect(() => {
    const handlePhotoUploaded = () => {
      refresh();
    };

    window.addEventListener('photoUploaded', handlePhotoUploaded);
    return () => {
      window.removeEventListener('photoUploaded', handlePhotoUploaded);
    };
  }, [refresh]);

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Sort photos chronologically by photo date (newest first)
  const sortedPhotos = [...photos].sort((a, b) => {
    const dateA = new Date(a.photoDate || a.uploadDate || a.createdAt);
    const dateB = new Date(b.photoDate || b.uploadDate || b.createdAt);
    return dateB.getTime() - dateA.getTime();
  });

  // Handle photo click - open in viewer
  const handlePhotoClick = (photo: ProgressPhoto) => {
    if (onPhotoClick) {
      onPhotoClick(photo);
    } else {
      setSelectedPhoto(photo);
    }
  };

  // Handle photo deletion
  const handleDeletePhoto = async (photoId: string) => {
    const { photoApi } = await import('@/services/api');
    
    try {
      const response = await photoApi.deletePhoto(photoId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete photo');
      }
      
      // Remove from cache optimistically
      removePhoto(photoId);
      
      return response;
    } catch (error) {
      console.error('Failed to delete photo:', error);
      throw error;
    }
  };

  // Handle photo navigation in viewer
  const handleNavigatePhoto = (direction: 'prev' | 'next') => {
    if (!selectedPhoto) return;
    
    const currentIndex = sortedPhotos.findIndex(p => p.id === selectedPhoto.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : sortedPhotos.length - 1;
    } else {
      newIndex = currentIndex < sortedPhotos.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSelectedPhoto(sortedPhotos[newIndex]);
  };

  // Authentication check
  if (!isAuthenticated) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="text-center py-12">
                <Camera className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">Sign in required</h3>
                <p className="text-sm text-muted-foreground">Please sign in to view your progress photos</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Loading state
  if (loading && photos.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="animate-pulse">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-square bg-muted rounded-lg"></div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-card border-border border-destructive/20">
            <CardContent className="p-6">
              <div className="text-center py-8">
                <Camera className="w-12 h-12 mx-auto mb-2 text-destructive/60" />
                <p className="font-medium text-destructive">Failed to load photos</p>
                <p className="text-sm text-destructive/70 mt-1">{error}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Empty state
  if (photos.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="text-center py-12">
                <Camera className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No photos yet</h3>
                <p className="text-sm text-muted-foreground mb-4">Start documenting your progress!</p>
                <p className="text-xs text-muted-foreground/70">Upload your first progress photo to get started</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                {/* Premium Photo Grid */}
                <motion.div 
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: {
                        staggerChildren: 0.1,
                      },
                    },
                  }}
                >
                  {sortedPhotos.map((photo, index) => (
                    <motion.div
                      key={photo.id}
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: { opacity: 1, y: 0 },
                      }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      whileHover={{ y: -2 }}
                      className="cursor-pointer"
                      onClick={() => handlePhotoClick(photo)}
                    >
                      <div className="relative aspect-square bg-muted rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                        {/* Photo Image */}
                        <img
                          src={photo.thumbnailUrl || photo.url}
                          alt={`Progress photo from ${formatDate(photo.photoDate || photo.uploadDate || photo.createdAt)}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            // Fallback to placeholder if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement?.querySelector('.photo-placeholder')?.classList.remove('hidden');
                          }}
                        />

                        {/* Placeholder for failed images */}
                        <div className="photo-placeholder hidden absolute inset-0 bg-muted">
                          <div className="flex items-center justify-center h-full">
                            <Camera className="w-8 h-8 text-muted-foreground" />
                          </div>
                        </div>

                        {/* Photo date overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <div className="text-white text-xs font-medium">
                            {formatDate(photo.photoDate || photo.uploadDate || photo.createdAt)}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Photo Viewer Modal */}
      <PhotoViewer
        photo={selectedPhoto}
        photos={sortedPhotos}
        onClose={() => setSelectedPhoto(null)}
        onDelete={handleDeletePhoto}
        onNavigate={handleNavigatePhoto}
      />
    </>
  );
}